<template>
  <PaymentCardSelect
    :items="items"
    :selectedPayment="selectedPayment"
    :hideDescription="hideDescription"
    @selectPayment="updateOrderWithPayment"
  />
</template>

<script setup>
// ====================================
// Packages
// ====================================
import { ref, watch } from "vue";
import { whenever } from "@vueuse/core";
// ====================================
// Storage
// ====================================
import { useOrderFormStore } from "@/stores/forms/orderFormStore";

import usePaymentFilters from "@/composables/filters/usePaymentFilters";
// ====================================
// Components
// ====================================
import PaymentCardSelect from "@/components/ui/selects/PaymentCardSelect.vue";

const orderStore = useOrderFormStore();
const { getDefaultPayment } = usePaymentFilters();

const props = defineProps({
  items: { type: Array, default: () => [] },
  label: {
    type: String,
    default: "Payment method",
  },
  hideDescription: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  paymentSelected: (payment) => typeof payment === "string",
});

const isLoading = ref(true);
const selectedPayment = ref(null);

const updateOrderWithPayment = (payment) => {
  if (!payment?.code) {
    return;
  }

  selectedPayment.value = payment.code.toLowerCase();
  emit("paymentSelected", { payment: selectedPayment.value });
  orderStore.setActiveModePaymentProvider(selectedPayment.value);
  orderStore.setPspTypeCode(payment.paymentType.toLowerCase());
  orderStore.setPspRequireMobile(payment.require_mobile);
};

const runOnMounted = (paymentOnMount) => {
  if (!paymentOnMount) {
    return;
  }

  isLoading.value = false;
  updateOrderWithPayment(paymentOnMount);
};

whenever(
  () =>
    orderStore.getActiveStore.form.coinType.value &&
    props.items.length > 0 &&
    !orderStore.isOrderFromMerchantApi &&
    orderStore.getActiveStorePaymentData.length > 0,
  () => {
    const paymentOnMount = orderStore.filterPaymentsByFiatAndCoin[0];
    runOnMounted(paymentOnMount);
  },
  { deep: true, immediate: true }
);

// Watch for B2B order
watch(
  () =>
    orderStore.isOrderFromMerchantApi &&
    orderStore.filterPaymentsByFiatAndCoin.length &&
    orderStore.getActiveStorePaymentData.length > 0,
  () => {
    const paymentOnMount = getDefaultPayment(
      orderStore.getMerchantApiData.payment,
      orderStore.filterPaymentsByFiatAndCoin
    );

    runOnMounted(paymentOnMount);
  },
  { deep: true, immediate: true }
);

watch(
  () => props.defaultItem,
  (paymentOnMount) => {
    if (paymentOnMount) {
      runOnMounted(paymentOnMount);
    }
  },
  { deep: true }
);
</script>
<style lang="scss"></style>
